<template>
  <div>
    <ShareLinkInvalidMessage v-if="showLinkExpiredMessage">
      This link is either invalid, or the goal is no longer shared by its owner. Ask the owner of the goal for a new shareable link.
    </ShareLinkInvalidMessage>
    <template v-else>
      <SharedPageBanner
        content-type-label="goal"
        :content-type="shareContentType"
        :user-name="savedAnchorPage.user_name"
        :student-name="student.name"
        :last-updated="savedAnchorPage.updated_at"
        :share-link="shareLink"
        v-if="showBanner"/>
      <CopyTrackWrapper
        type="shared_superpage"
        :title="savedAnchorPage.title"
        :uuid="savedAnchorPage.uuid"
        :class="['content-page', {'anchor-page': hasAccordionObjectives}]">
        <div class="container">
          <div class="row">
            <!-- Main Content -->
            <div class="col-sm-8">
              <h1>{{ savedAnchorPage.title }}</h1>
              <div :class="{'page-labels': hasAccordionObjectives}">
                <AnchorPageLabel v-if="!hasAccordionObjectives" size="md" />&nbsp;
                <span class="label label-success"><i class="fa fa-user"/> Saved to <span class="fs-block">{{ student.name }}</span></span>
              </div>
              <h2 v-if="!hasAccordionObjectives">Annual Target</h2>
              <BigGoal
                :footer="showAnnualGoalFooter ? true : false"
                :footer2="hasAccordionObjectives"
                :event-context="{ obj_level: 'Annual' }"
              >
                <CopyTrackWrapper
                  slot="body"
                  v-if="savedAnchorPage.grade_level_goal"
                  type="annual_target"
                  level="Annual"
                  :title="savedAnchorPage.title"
                  :uuid="savedAnchorPage.uuid"
                  :class="{ 'anchor-page-wiki-text': hasAccordionObjectives }"
                >
                  <WikiText
                    :text="savedAnchorPage.grade_level_goal"
                    :strategies="strategyMap"
                    :object-id="pageContent.uuid"
                  />
                </CopyTrackWrapper>

                <template
                  v-slot:footer
                  v-if="hasPMAs">
                  <ResourceListContainer
                    :uuids="pageContent.goal_resources"
                    :is-indented="hasAccordionObjectives"
                    :show-resource-tile="hasAccordionObjectives"
                    :object-id="pageContent.uuid"
                  />
                </template>

                <template
                  v-if="hasAccordionObjectives"
                  v-slot:footer2>
                  <GoalExampleContent
                    @toggle="toggledQuarterlyObjectives"
                    :initially-expand="true"
                    :is-indented="hasAccordionObjectives">
                    <template v-slot:label>
                      <img
                        src="~@/assets/quarterly-objectives-icon.svg"
                        class="objectives-icon--button"
                        aria-hidden="true">Objectives
                    </template>
                    <AccordionObjectivesList
                      :access-info="accessInfo"
                      :print-permissions="actionPermissions.printAssessment"
                      :page-uuid="pageContent.uuid"
                      :objectives="pageContent.objectives"
                      :editable="false"
                    />
                  </GoalExampleContent>
                </template>

                <template
                  v-slot:footer2
                  v-else-if="pageContent.goal_example_text">
                  <GoalExampleContent @toggle="clickedExampleContent">
                    <CopyTrackWrapper
                      type="goal-example"
                      level="Annual"
                      :title="savedAnchorPage.title"
                      :uuid="savedAnchorPage.uuid">
                      <WikiText
                        :text="pageContent.goal_example_text"
                      />
                    </CopyTrackWrapper>
                  </GoalExampleContent>
                </template>
              </BigGoal>

              <template v-if="hasEditableObjectives && !hasAccordionObjectives">
                <h2>Quarterly Objectives</h2>
                <AnchorPageObjectives
                  :items="pageContent.assessment_items"
                  :access-info="Object.assign(accessInfo, {blur: false})"
                  :print-permissions="actionPermissions.printAssessment"
                  :page-uuid="pageContent.uuid"
                  :objectives="savedAnchorPage.objectives"
                  :editable="false"
                  :objective-resource-uuids="pageContent.objective_resources"/>
              </template>
            </div>

            <!-- Side Bar -->
            <div class="col-sm-4">

              <!-- Standards/Staircase -->
              <template v-if="fetchingActiveProfile || fetchingStandards || fetchingStaircase">
                <p class="text-muted"><i class="fa fa-spin fa-circle-o-notch" /> Loading...</p>
              </template>

              <template v-if="savedAnchorPage.user_content_profile_id === profileId">
                <template v-if="keyStandard">
                  <h2>{{ keyStandard.standard_set }}</h2>
                  <CopyTrackWrapper
                    type="standard"
                    :title="savedAnchorPage.title"
                    :uuid="savedAnchorPage.uuid">
                    <PowerStandard
                      v-if="!hasAccordionObjectives"
                      :standard="keyStandard"/>
                    <StandardsAccordion
                      v-else
                      :standards="keyStandardArray"
                      :should-expand-first-standard="true"/>
                  </CopyTrackWrapper>
                </template>
                <GoalContentPageStandards
                  :show-edit-link="editorVisibility"
                  :content-profile-id="profileId"
                  :grouped-standards="groupedSidebarStandards"
                  :should-expand-first-standard="!hasAccordionObjectives"
                />

                <template v-if="staircaseContent && staircaseContent.length > 0">
                  <h2 :class="{ 'sidebar-section': hasAccordionObjectives }">Standard Staircase</h2>
                  <CopyTrackWrapper
                    type="staircase"
                    :title="pageContent.title"
                    :uuid="pageContent.uuid"
                    :demo="pageContent.demo">
                    <Staircase
                      :page-uuid="pageContent.uuid"
                      :grades="pageContent.grades"
                      :goal-staircase="staircaseContent"
                      :show-anchor-page-label="!hasAccordionObjectives"
                    />
                  </CopyTrackWrapper>
                </template>
              </template>

            </div>
          </div>

          <SubmitFeedback
            :title="savedAnchorPage.title"
            :uuid="savedAnchorPage.uuid"
            type="Shared Anchor Page"
            :subject="pageContent.subject" />

          <TermsOfUseFooter />
        </div>

        <div class="print-message">This page is intentionally left blank.</div>

      </CopyTrackWrapper>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import {
  anchorPageMixin,
  goalSetContentProfileMixin
} from '@/components/goals/common/goal_content_mixin'
import SharedPageBanner from '@/components/SharedPageBanner'
import AnchorPageObjectives from '@/components/goals/AnchorPageObjectives'
import ShareLinkInvalidMessage from '@/components/library/ShareLinkInvalidMessage'
import TermsOfUseFooter from '@/components/library/TermsOfUseFooter'
import StandardsAccordion from '@/components/goals/StandardsAccordion'
import { setTitle } from '@/util/title'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { getSharedGoal } from '@/api/library'
import { pmaSvg } from '@/icons'

export default {
  name: 'SharedAnchorPage',
  mixins: [anchorPageMixin, goalSetContentProfileMixin],
  components: {
    SharedPageBanner,
    AnchorPageObjectives,
    ShareLinkInvalidMessage,
    TermsOfUseFooter,
    StandardsAccordion,
  },
  props: {
    uuid: {
      required: true,
      type: String
    }
  },
  data() {
    return {
      savedAnchorPage: {},
      shareContentType: CONTENT_TYPES.SAVED_SUPER_PAGE,

      student: {
        name: null,
        uuid: null
      },

      showBanner: false,
      showLinkExpiredMessage: false,
      pmaSvg: pmaSvg
    }
  },
  computed: {
    ...mapGetters('visibility', ['editorVisibility']),
    hasEditableObjectives() {
      return this.savedAnchorPage.objectives && this.savedAnchorPage.objectives.length > 0
    },
    shareLink() {
      return (
        window.location.origin +
        this.$router.resolve({
          name: 'SharedAnchorPage',
          params: { uuid: this.uuid }
        }).href
      )
    }
  },
  methods: {
    ...mapActions('goaldetail', ['fetchAllResources']),
    async loadPageContent() {
      try {
        let resp = await getSharedGoal(this.uuid, {
          author: true
        })
        this.savedAnchorPage = resp
        let ownersProfileId = this.savedAnchorPage.user_content_profile_id
        if (ownersProfileId) {
          this.setActiveProfileById(ownersProfileId)
        }
        setTitle(`${this.savedAnchorPage.title} | Shared Anchor Page`)
        this.student.name = resp.student_name
        this.student.uuid = resp.student_uuid
        await this.fetchPageContent({
          type: CONTENT_TYPES.SUPER_PAGE,
          attribute: 'uuid',
          value: this.savedAnchorPage.goal_uuid
        })

        await this.fetchAllResources()

        this.showBanner = true

        this.$sendEvent('Viewed Shared Library Anchor Page')
      } catch (err) {
        console.error(err)
        this.showLinkExpiredMessage = true
      }
    }
  },
  eventContext() {
    return {
      goal_subject: this.pageContent.subject,
      goal_subject_domains: this.pageContent.subject_domains,
      goal_grades: this.pageContent.grades,
      goal_uuid: this.pageContent.uuid,
      goal_title: this.pageContent.title,
      goal_standards:
        this.pageContent.standard_set_refs &&
        this.pageContent.standard_set_refs.map(setRef => setRef.standard_long_ref),
      goal_author_id: this.pageContent.author_id,
      goal_is_anchor: this.pageContent.super_page,
      goal_has_objectives: this.hasObjectives,
      goal_is_library: true,
      is_library: true,
      student_uuid: this.student.uuid,
      saved_goal_uuid: this.uuid
    }
  },
  watch: {
    $route: function() {
      this.loadPageContent()
    },
  },
  created() {
    this.loadPageContent()
  },
  beforeDestroy() {
    this.setActiveProfileById(this.userContentProfileId)
  }
}
</script>

<style scoped lang="less">
@import '~@/variables.less';
h1 small {
  font-size: 18px;
}
.label {
  padding: 0.3em 0.7em 0.4em;
  font-size: 85%;
}
.page-labels {
  margin-bottom: 8px;
}

/deep/ .panel-body {
  padding-bottom: 0;
}
</style>
